import React, { useEffect, useMemo, useState } from 'react';
import { Button, Drawer, Form, Input, InputNumber, message, Select, Space } from 'antd';
import dayjs from 'dayjs';
import { useSnapshot } from 'valtio';
import { changePointApi } from '@/api/member/member';
import { state } from '@/store';

const changeTypeList = [
  { label: '增加', value: 'ADD' },
  { label: '减少', value: 'SUB' },
];

interface Props {
  open: boolean;
  onClose: () => void;
  oneId: string;
  memberCardId?: string | number;
  shopId?: string | number;
  currentPoint?: number;
  channelType?: string;
  mobile?: string;
  platformId?: string;
  onSuccess?: () => void;
}

const SNOWFLAKE_EPOCH = 1704067200000;
const SNOWFLAKE_SEQUENCE_MAX = 4095;
let lastSnowflakeTimestamp = 0;
let snowflakeSequence = 0;

const multiplyDecimalStringByInt = (value: string, multiplier: number) => {
  if (!value || value === '0' || multiplier === 0) return '0';
  let carry = 0;
  let result = '';
  for (let index = value.length - 1; index >= 0; index -= 1) {
    const current = Number(value[index]) * multiplier + carry;
    result = String(current % 10) + result;
    carry = Math.floor(current / 10);
  }
  while (carry > 0) {
    result = String(carry % 10) + result;
    carry = Math.floor(carry / 10);
  }
  return result.replace(/^0+/, '') || '0';
};

const addIntToDecimalString = (value: string, addend: number) => {
  let carry = addend;
  let result = '';
  for (let index = value.length - 1; index >= 0; index -= 1) {
    const current = Number(value[index]) + (carry % 10);
    result = String(current % 10) + result;
    carry = Math.floor(carry / 10) + Math.floor(current / 10);
  }
  while (carry > 0) {
    result = String(carry % 10) + result;
    carry = Math.floor(carry / 10);
  }
  return result.replace(/^0+/, '') || '0';
};

const nextSnowflakeTimestamp = (current: number) => {
  let next = Date.now();
  while (next <= current) {
    next = Date.now();
  }
  return next;
};

const generateSnowflakeId = (workerSeed?: string | number) => {
  let timestamp = Date.now();
  if (timestamp < lastSnowflakeTimestamp) {
    timestamp = lastSnowflakeTimestamp;
  }
  if (timestamp === lastSnowflakeTimestamp) {
    snowflakeSequence = (snowflakeSequence + 1) & SNOWFLAKE_SEQUENCE_MAX;
    if (snowflakeSequence === 0) {
      timestamp = nextSnowflakeTimestamp(lastSnowflakeTimestamp);
    }
  } else {
    snowflakeSequence = 0;
  }
  lastSnowflakeTimestamp = timestamp;

  const workerNumber = Number(workerSeed);
  const workerId = Number.isFinite(workerNumber) ? Math.abs(workerNumber) % 1024 : 1;
  const timestampPart = multiplyDecimalStringByInt(String(timestamp - SNOWFLAKE_EPOCH), 4194304);
  return addIntToDecimalString(timestampPart, workerId * 4096 + snowflakeSequence);
};

const ChangePointDrawer: React.FC<Props> = ({
  open,
  onClose,
  oneId,
  memberCardId,
  shopId,
  currentPoint,
  channelType,
  mobile,
  platformId,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const snap = useSnapshot(state);
  const changeTypeValue = Form.useWatch('changeType', form);

  const memberCardValue = useMemo(() => {
    const current = shopId || snap.currentShopId;
    return current ? String(current) : undefined;
  }, [shopId, snap.currentShopId]);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue({
      oneId,
      memberCard: memberCardValue,
      currentPoint: typeof currentPoint === 'number' ? currentPoint : '-',
      changeType: undefined,
      changeQuantity: undefined,
      remark: '',
    });
  }, [open, form, oneId, memberCardValue, currentPoint]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (!memberCardId) {
        message.warning('缺少会员卡信息');
        return;
      }
      setSubmitting(true);
      const res = await changePointApi({
        id: generateSnowflakeId(memberCardValue || shopId),
        channelType: channelType || '',
        shopId: memberCardValue || '',
        memberCardId,
        oneId,
        phone: mobile || '',
        platformId: platformId || '',
        orderId: '',
        refundId: '',
        changeType: values.changeType,
        tradeType: 'NON_TRADE',
        changeQuantity: Number(values.changeQuantity),
        changeCause: '手动变更',
        remark: values.remark,
        changeTime: dayjs().format('YYYY-MM-DD HH:mm:ss'),
      });
      if (res?.code === 200) {
        message.success('积分调整成功');
        onClose();
        onSuccess?.();
      }
    } catch (error: any) {
      if (error?.errorFields) return;
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      destroyOnHidden
      open={open}
      title="积分变更"
      width={420}
      onClose={onClose}
      extra={
        <Space>
          <Button onClick={onClose}>取消</Button>
          <Button
            type="primary"
            loading={submitting}
            onClick={handleSubmit}>
            确定
          </Button>
        </Space>
      }>
      <Form
        form={form}
        layout="vertical">
        <Form.Item
          label="OneID"
          name="oneId">
          <Input disabled />
        </Form.Item>
        <Form.Item
          label="会员卡"
          name="memberCard">
          <Select
            disabled
            placeholder="请选择"
            options={snap.shopList?.map((item: any) => ({ label: item.shopName, value: String(item.shopId) }))}
          />
        </Form.Item>
        <Form.Item
          label="当前积分"
          name="currentPoint">
          <Input disabled />
        </Form.Item>
        <Form.Item
          label="变更类型"
          required>
          <Space
            size={12}
            style={{ width: '100%' }}>
            <Form.Item
              name="changeType"
              rules={[{ required: true, message: '请选择变更类型' }]}
              noStyle>
              <Select
                style={{ width: 100 }}
                placeholder="请选择"
                options={changeTypeList}
              />
            </Form.Item>
            <Form.Item
              name="changeQuantity"
              rules={[
                { required: true, message: '请输入积分数量' },
                () => ({
                  validator(_, value) {
                    if (!value || Number(value) > 0) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('请输入大于 0 的积分数量'));
                  },
                }),
              ]}
              noStyle>
              <InputNumber
                min={1}
                style={{ width: 258 }}
                precision={0}
                placeholder="请输入积分"
                addonAfter="积分"
                disabled={!changeTypeValue}
              />
            </Form.Item>
          </Space>
        </Form.Item>
        <Form.Item
          label="备注"
          name="remark"
          rules={[{ required: true, message: '请输入备注' }]}>
          <Input.TextArea
            rows={3}
            maxLength={100}
            showCount
            placeholder="请输入备注"
          />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default ChangePointDrawer;
